import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useNotification } from '@/components/notifications';
import { useGetEncryptionStatus } from '@/hooks/queries/use-get-encryption-status';
import { useDeleteEncryptionConfig } from '@/hooks/mutations/encryption/use-delete-encryption-config';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { RefreshCw, Pencil } from 'lucide-react';
import * as Sentry from '@sentry/react';

interface EncryptionStatusTabProps {
  onEditConfig: () => void;
}

export const EncryptionStatusTab = ({
  onEditConfig,
}: EncryptionStatusTabProps) => {
  const notification = useNotification();
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: status, isLoading } = useGetEncryptionStatus(
    refreshKey > 0,
    refreshKey,
  );
  const deleteConfig = useDeleteEncryptionConfig();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleDelete = async () => {
    try {
      await deleteConfig.mutateAsync();
      notification.success('Encryption configuration removed');
      setIsDeleteModalOpen(false);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'encryption-delete-config' },
      });
      notification.error(
        error instanceof Error
          ? error.message
          : 'Failed to remove encryption configuration',
      );
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <LoadingSpinner />
      </div>
    );
  }

  if (!status?.configured) {
    return (
      <div className='flex flex-col items-center justify-center rounded-lg border border-dashed py-12'>
        <p className='text-muted-foreground mb-4'>
          Encryption is not configured for this organization.
        </p>
        <Button onClick={onEditConfig}>Configure Encryption</Button>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <h4 className='text-lg font-medium'>Encryption Status</h4>
        <Button variant='outline' size='sm' onClick={handleRefresh}>
          <RefreshCw className='size-4' />
          Refresh
        </Button>
      </div>

      <div className='grid grid-cols-2 gap-4 rounded-lg border p-4'>
        <div>
          <p className='text-sm text-muted-foreground'>Vault URL</p>
          <p className='text-sm font-medium break-all'>{status.vault_url}</p>
        </div>
        <div>
          <p className='text-sm text-muted-foreground'>Key Name</p>
          <p className='text-sm font-medium'>{status.key_name}</p>
        </div>
        <div>
          <p className='text-sm text-muted-foreground'>Current KEK Version</p>
          <p className='text-sm font-medium'>
            {status.current_kek_version ?? 'N/A'}
          </p>
        </div>
        <div>
          <p className='text-sm text-muted-foreground'>Last Rotation Date</p>
          <p className='text-sm font-medium'>
            {status.last_rotation_at
              ? new Date(status.last_rotation_at).toLocaleString()
              : 'Never'}
          </p>
        </div>
      </div>

      {status.buckets && status.buckets.length > 0 && (
        <div className='space-y-3'>
          <h4 className='text-lg font-medium'>Bucket Statistics</h4>
          <div className='border rounded-lg'>
            <table className='w-full'>
              <thead>
                <tr className='border-b bg-muted/50'>
                  <th className='text-left px-4 py-3 text-sm font-medium text-muted-foreground'>
                    Bucket
                  </th>
                  <th className='text-left px-4 py-3 text-sm font-medium text-muted-foreground'>
                    Rewrapped
                  </th>
                  <th className='text-left px-4 py-3 text-sm font-medium text-muted-foreground'>
                    Failed
                  </th>
                  <th className='text-left px-4 py-3 text-sm font-medium text-muted-foreground'>
                    Skipped
                  </th>
                  <th className='text-left px-4 py-3 text-sm font-medium text-muted-foreground'>
                    Last Run
                  </th>
                </tr>
              </thead>
              <tbody>
                {status.buckets.map((bucket) => (
                  <tr
                    key={bucket.name}
                    className='border-b last:border-b-0 hover:bg-muted/30 transition-colors'
                  >
                    <td className='px-4 py-3 text-sm font-medium'>
                      {bucket.name}
                    </td>
                    <td className='px-4 py-3 text-sm'>{bucket.rewrapped}</td>
                    <td className='px-4 py-3 text-sm'>
                      {bucket.failed > 0 ? (
                        <span className='text-red-600 dark:text-red-400'>
                          {bucket.failed}
                        </span>
                      ) : (
                        bucket.failed
                      )}
                    </td>
                    <td className='px-4 py-3 text-sm'>{bucket.skipped}</td>
                    <td className='px-4 py-3 text-sm text-muted-foreground'>
                      {bucket.last_run_at
                        ? new Date(bucket.last_run_at).toLocaleString()
                        : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className='flex items-center gap-3 pt-2'>
        <Button variant='outline' onClick={onEditConfig}>
          <Pencil className='size-4' />
          Edit Configuration
        </Button>
        <Button
          variant='outline'
          className='text-destructive hover:text-destructive'
          onClick={() => setIsDeleteModalOpen(true)}
        >
          Remove Configuration
        </Button>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        isLoading={deleteConfig.isPending}
        title='Remove encryption configuration'
        description='Are you sure you want to remove the encryption configuration? This will not decrypt existing data, but new data will no longer be encrypted.'
        buttonLabel='Remove configuration'
        handleDelete={handleDelete}
        handleCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};
